import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentService, Document, DocumentsPagedResult } from '../services/document.service';
import { App } from 'antd';

export const documentKeys = {
  all: ['documents'] as const,
  lists: (filters?: any) => [...documentKeys.all, 'list', filters] as const,
  list: (filters?: any) => [...documentKeys.all, 'list', filters] as const,
  detail: (id: string) => [...documentKeys.all, 'detail', id] as const,
  byEntity: (entityType: string, entityId: string) => [...documentKeys.all, 'entity', entityType, entityId] as const,
  paged: (filters: Record<string, unknown>, page: number, pageSize: number) =>
    [...documentKeys.all, 'paged', filters, page, pageSize] as const,
};

export function useDocuments(filters?: {
  entity_type?: string;
  entity_id?: string;
  folder_id?: string;
  document_type?: string;
  document_status?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: documentKeys.list(filters),
    queryFn: () => documentService.getDocuments(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function useDocumentsByEntity(entityType: string, entityId: string) {
  const queryKey = documentKeys.byEntity(entityType, entityId);

  return useQuery({
    queryKey,
    queryFn: () => documentService.getDocumentsByEntity(entityType, entityId),
    enabled: !!entityType && !!entityId,
    staleTime: 0, // Always refetch when invalidated
    refetchOnWindowFocus: true,
  });
}

export function useDocumentsPaged(
  filters: {
    search?: string;
    entity_type?: string;
    document_type?: string;
    document_status?: string;
  },
  page: number,
  pageSize: number
) {
  const stableFilters = {
    search: filters.search?.trim() || undefined,
    entity_type: filters.entity_type?.trim() || undefined,
    document_type: filters.document_type?.trim() || undefined,
    document_status: filters.document_status?.trim() || undefined,
  };
  return useQuery({
    queryKey: documentKeys.paged(stableFilters, page, pageSize),
    queryFn: (): Promise<DocumentsPagedResult> =>
      documentService.getDocumentsPaged({
        page,
        limit: pageSize,
        ...stableFilters,
      }),
    placeholderData: (prev) => prev,
    staleTime: 30 * 1000,
  });
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: documentKeys.detail(id),
    queryFn: () => documentService.getDocumentById(id),
    enabled: !!id,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({
      file,
      data,
    }: {
      file: File;
      data: {
        document_name?: string;
        entity_type?: string;
        entity_id?: string;
        document_type?: string;
        document_category?: string;
        folder_id?: string;
        description?: string;
        tags?: string[];
        keywords?: string;
        is_confidential?: boolean;
        access_level?: string;
      };
    }) => documentService.uploadDocument(file, data),
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: documentKeys.all });
      if (variables.data.entity_type && variables.data.entity_id) {
        await queryClient.invalidateQueries({
          queryKey: documentKeys.byEntity(variables.data.entity_type, variables.data.entity_id),
        });
        await queryClient.refetchQueries({
          queryKey: documentKeys.byEntity(variables.data.entity_type, variables.data.entity_id),
        });
      }
      message.success('Document uploaded successfully');
    },
    onError: () => message.error('Failed to upload document'),
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Document> }) =>
      documentService.updateDocument(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      message.success('Document updated successfully');
    },
    onError: () => message.error('Failed to update document'),
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: (id: string) => documentService.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      message.success('Document deleted successfully');
    },
    onError: () => message.error('Failed to delete document'),
  });
}

export function useLinkDocument() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  return useMutation({
    mutationFn: ({ id, entityType, entityId }: { id: string; entityType: string; entityId: string }) =>
      documentService.linkDocument(id, entityType, entityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({
        queryKey: documentKeys.byEntity(variables.entityType, variables.entityId),
      });
      message.success('Document linked successfully');
    },
    onError: () => message.error('Failed to link document'),
  });
}

